/**
 * Thin client for the PHP portal API.
 *
 * Auth is a plain HttpOnly session cookie, so nothing sensitive is ever kept in
 * JavaScript. The CSRF token is the one piece of state we hold: the server
 * hands it over on `me` / `login` and expects it back on every mutation.
 */

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

let csrfToken = null;

export function setCsrfToken(token) {
  if (token) {
    csrfToken = token;
  }
}

/**
 * Errors carry a machine-readable `code` alongside the message the server wants
 * shown to the user. A plain Error with extra fields rather than a subclass —
 * `class X extends Error` does not survive this project's ES5 transpile target
 * in a way that keeps `instanceof` working.
 */
export function ApiError(message, code, status) {
  const error = new Error(message);
  error.name = 'ApiError';
  error.code = code;
  error.status = status;
  return error;
}

/**
 * Parse a JSON body that a misconfigured host may have prefixed with HTML.
 *
 * Some shared hosts force `display_errors` on, and a request-startup warning
 * (the classic one: a POST larger than post_max_size) is printed before our
 * code can suppress it. The JSON that follows is perfectly good, and it carries
 * the message the user needs — so recover it rather than throwing away a
 * useful error in favour of "unexpected response".
 */
function parseJsonBody(text) {
  try {
    return JSON.parse(text);
  } catch (e) {
    const start = text.search(/[[{]/);
    if (start > 0) {
      try {
        return JSON.parse(text.slice(start));
      } catch (inner) {
        /* fall through */
      }
    }
    throw e;
  }
}

async function parse(response) {
  const text = await response.text();
  let data = null;
  try {
    data = text ? parseJsonBody(text) : null;
  } catch (e) {
    // A PHP fatal or an HTML error page from the host — surface something the
    // webmaster can actually act on rather than "Unexpected token <".
    throw ApiError(
      'The server returned an unexpected response. Check the API is deployed at ' + API_BASE + '.',
      'bad_response',
      response.status
    );
  }

  if (!response.ok) {
    const message = data && data.message ? data.message : 'Request failed (' + response.status + ').';
    const code = data && data.error ? data.error : 'http_' + response.status;
    throw ApiError(message, code, response.status);
  }

  return data;
}

async function request(path, options) {
  const opts = options || {};
  const headers = Object.assign({}, opts.headers);

  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (opts.method === 'POST' && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  let response;
  try {
    response = await fetch(API_BASE + path, {
      method: opts.method || 'GET',
      credentials: 'include',
      headers,
      body: opts.body instanceof FormData ? opts.body : opts.body ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    throw ApiError('Could not reach the server. Check your internet connection.', 'network', 0);
  }

  return parse(response);
}

const get = (path) => request(path);
const post = (path, body) => request(path, { method: 'POST', body });

// --- Auth ------------------------------------------------------------------

export const auth = {
  me: () => get('/auth.php?action=me'),
  login: (email, password) => post('/auth.php?action=login', { email, password }),
  logout: () => post('/auth.php?action=logout', {}),
  changePassword: (currentPassword, newPassword) =>
    post('/auth.php?action=change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
};

// --- Gallery ---------------------------------------------------------------

export const gallery = {
  list: (includeHidden) => get('/gallery.php?action=list' + (includeHidden ? '&all=1' : '')),
  upload: (file, fields, onProgress) => uploadWithProgress(file, fields, onProgress),
  update: (id, changes) => post('/gallery.php?action=update', Object.assign({ id }, changes)),
  remove: (id) => post('/gallery.php?action=delete', { id }),
  reorder: (ids) => post('/gallery.php?action=reorder', { ids }),
};

/**
 * XHR rather than fetch, purely because fetch still has no upload progress
 * event — and a photo from a phone on mobile data needs a progress bar.
 *
 * @param field  form field name the endpoint expects
 * @param path   endpoint path under the API base
 */
function uploadWithProgress(file, fields, onProgress, field, path) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append(field || 'photo', file);
    Object.keys(fields || {}).forEach((key) => form.append(key, fields[key]));

    const xhr = new XMLHttpRequest();
    xhr.open('POST', API_BASE + (path || '/gallery.php?action=upload'), true);
    xhr.withCredentials = true;
    if (csrfToken) {
      xhr.setRequestHeader('X-CSRF-Token', csrfToken);
    }

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      let data = null;
      try {
        data = xhr.responseText ? parseJsonBody(xhr.responseText) : null;
      } catch (e) {
        reject(ApiError('The server returned an unexpected response during upload.', 'bad_response', xhr.status));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
      } else {
        const error = ApiError(
          data && data.message ? data.message : 'The upload failed (' + xhr.status + ').',
          data && data.error ? data.error : 'upload_failed',
          xhr.status
        );
        // Rejected deploy packages come back with the offending paths attached;
        // the panel needs them to explain what was wrong.
        error.data = data;
        reject(error);
      }
    };

    xhr.onerror = () => reject(ApiError('The upload was interrupted. Please try again.', 'network', 0));
    xhr.onabort = () => reject(ApiError('The upload was cancelled.', 'aborted', 0));

    xhr.send(form);
  });
}

// --- Webmaster tools -------------------------------------------------------

export const health = {
  status: () => get('/health.php?action=status'),
  ping: () => get('/health.php?action=ping'),
};

export const logs = {
  list: (params) => {
    const p = params || {};
    const query = ['limit=' + encodeURIComponent(p.limit || 200)];
    if (p.level) query.push('level=' + encodeURIComponent(p.level));
    if (p.q) query.push('q=' + encodeURIComponent(p.q));
    return get('/logs.php?action=list&' + query.join('&'));
  },
  clear: () => post('/logs.php?action=clear', {}),
  ingest: (level, message, meta) => post('/logs.php?action=ingest', { level, message, meta }),
};

export const updates = {
  list: () => get('/update.php?action=list'),
  apply: (file, onProgress) => uploadWithProgress(file, {}, onProgress, 'package', '/update.php?action=apply'),
  rollback: (id) => post('/update.php?action=rollback', { id }),
};

export const invites = {
  list: () => get('/invite.php?action=list'),
  send: (email, name) => post('/invite.php?action=send', { email, name }),
  revoke: (id) => post('/invite.php?action=revoke', { id }),
  removeAccount: (id) => post('/invite.php?action=remove-account', { id }),
  verify: (token) => get('/invite.php?action=verify&token=' + encodeURIComponent(token)),
  accept: (token, name, password) => post('/invite.php?action=accept', { token, name, password }),
};
